import { UserPermissions } from '@creditsights/api-ui-web';
import { InitialActionAllower, RouteToCallback } from '@creditsights/styleguide-web/components/DataView';
import { MouseEvent } from 'react';
import { DataViewConfig, ExpandedViewConfig, TableViewConfig } from '@creditsights/styleguide-web';
import { ROUTES } from '../../../Base.constants';
import {
  ArticleTitleSummaryFormatter,
  CompaniesFormatter,
  DateRenderer,
  GhostLinkRenderer,
  TitleWithContextMenuRenderer,
} from '../../../components/formatters';
import { ContentTypeRenderer } from '../../../components/formatters/ContentTypeRenderer';
import { LockedArticleLabel } from '../../../components/RestrictedArticleAccess/LockedArticle';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import {
  ARTICLE_TYPE_COLUMN_WIDTH,
  CONTEXT_MENU_COLUMN_WIDTH,
  DATE_COLUMN_WIDTH,
} from '../../../utils/columnConfigConstants';
import { SummaryRenderer } from '../../../components/formatters/SummaryRenderer';
import { ContextMenuRenderer } from '../../../components/formatters/ContextMenuRenderer';

const columnsConfig: (
  routeToCallback: RouteToCallback,
  shouldAllowInitialAction: InitialActionAllower,
  userPermissions: UserPermissions,
  isTypeShown: boolean
) => TableViewConfig = (routeToCallback, shouldAllowInitialAction, userPermissions, isTypeShown) => {
  const columns = [
    {
      dataKey: 'content',
      cellLinkRenderer: GhostLinkRenderer,
      cellDataRenderer: ArticleTitleSummaryFormatter,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'companies',
      // TODO: Switch to TickersFormatter when Ticker data is fixed
      cellDataRenderer: CompaniesFormatter({ isBold: true }),
      cellClassname: 'cell-company',
      dataViewItemConfiguration: { stopMouseUpPropagation: true, shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'publishDate',
      cellDataRenderer: DateRenderer,
      width: DATE_COLUMN_WIDTH,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'id',
      cellDataRenderer: ContextMenuRenderer,
      width: CONTEXT_MENU_COLUMN_WIDTH,
      stopMouseUpPropagation: true,
    },
  ];

  if (isTypeShown) {
    columns.splice(2, 0, {
      dataKey: 'articleType',
      width: ARTICLE_TYPE_COLUMN_WIDTH,
      cellDataRenderer: ContentTypeRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    });
  }

  return {
    columns,
    disabled: {
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
      dataRendererProps: { userPermissions },
    },
  };
};

// const EXPANDED_VIEW_COMPANY_COUNT = 3;

const expandedConfig: (
  shouldAllowInitialAction: InitialActionAllower,
  routeToCallback: RouteToCallback,
  userPermissions: UserPermissions,
  isTypeShown: boolean
) => ExpandedViewConfig = (shouldAllowInitialAction, routeToCallback, userPermissions, isTypeShown) => {
  const articleType = {
    dataKey: 'articleType',
    dataRenderer: ContentTypeRenderer,
    dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
  };

  const subtitle = [
    {
      renderWithoutDataValue: true,
      dataRenderer: GhostLinkRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'publishDate',
      dataRenderer: DateRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'companies',
      // TODO: Switch to TickersFormatter when Ticker data is fixed
      // dataRenderer: TickersFormatter({ companyCount: EXPANDED_VIEW_COMPANY_COUNT }),
      dataRenderer: CompaniesFormatter(),
      dataViewItemConfiguration: {
        stopMouseUpPropagation: true,
        shouldAllowInitialAction,
        routeToCallback,
      },
    },
  ];

  return {
    title: {
      dataKey: 'content.title',
      dataRenderer: TitleWithContextMenuRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    subtitle: isTypeShown ? [articleType, ...subtitle] : subtitle,
    content: {
      dataKey: 'content.summary',
      dataRenderer: SummaryRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    disabled: {
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
      dataRendererProps: { userPermissions },
    },
  };
};

export const viewConfig = (
  onPreviewableArticleClick: (article: ArticleTableRow, event: MouseEvent<HTMLElement>) => void,
  isPreviewable: boolean,
  userPermissions: UserPermissions,
  isTypeShown: boolean
): DataViewConfig<ArticleTableRow> => {
  const routeToCallback: RouteToCallback = (articleId) => ROUTES.article(articleId);
  const shouldAllowInitialAction: InitialActionAllower = (row) => isPreviewable || !!row?.isDisabled;

  return {
    tableViewConfig: columnsConfig(routeToCallback, shouldAllowInitialAction, userPermissions, isTypeShown),
    expandedViewConfig: expandedConfig(shouldAllowInitialAction, routeToCallback, userPermissions, isTypeShown),
    onRowMouseUp: onPreviewableArticleClick,
    getIsDisabled: (article: ArticleTableRow) => !article.canAccess,
    disabledItemElement: LockedArticleLabel,
  };
};
